import { useEffect, useState } from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type SolverPreset,
  type SolverPresetName,
  listSolverPresets,
} from "@/api/planning";

export type GenerateOptionsResult = {
  preset?: SolverPresetName;
  weight_carence?: number;
  weight_avoid?: number;
  weight_wish?: number;
  weight_equity?: number;
};

const PRESETS_ORDER: SolverPresetName[] = [
  "BALANCED",
  "MAX_COVERAGE",
  "MAX_EQUITY",
  "RESPECT_WISHES",
];

export function GenerateOptionsDialog({
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (options: GenerateOptionsResult) => void;
}) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<SolverPresetName>("BALANCED");
  const [advanced, setAdvanced] = useState(false);
  const [carence, setCarence] = useState(100);
  const [avoid, setAvoid] = useState(5);
  const [wish, setWish] = useState(5);
  const [equity, setEquity] = useState(1);

  const presets = useQuery({
    queryKey: ["solver-presets"],
    queryFn: listSolverPresets,
    enabled: open,
    staleTime: 60_000,
  });

  // When the user picks a preset, snap the sliders to its values.
  useEffect(() => {
    const row = (presets.data ?? []).find((p) => p.name === preset);
    if (row) {
      setCarence(row.carence);
      setAvoid(row.avoid);
      setWish(row.wish);
      setEquity(row.equity);
    }
  }, [preset, presets.data]);

  const handleConfirm = () => {
    const result: GenerateOptionsResult = { preset };
    if (advanced) {
      result.weight_carence = carence;
      result.weight_avoid = avoid;
      result.weight_wish = wish;
      result.weight_equity = equity;
    }
    onConfirm(result);
  };

  const presetByName = new Map<SolverPresetName, SolverPreset>(
    (presets.data ?? []).map((p) => [p.name, p]),
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>
        <AutoAwesomeIcon
          fontSize="small"
          sx={{ verticalAlign: "middle", mr: 1, color: "primary.main" }}
        />
        {t("generate.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="info">{t("generate.body")}</Alert>

          <FormControl>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t("generate.presetLabel")}
            </Typography>
            <RadioGroup
              value={preset}
              onChange={(e) => setPreset(e.target.value as SolverPresetName)}
            >
              {PRESETS_ORDER.map((name) => {
                const row = presetByName.get(name);
                return (
                  <FormControlLabel
                    key={name}
                    value={name}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t(`generate.preset.${name}.label`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`generate.preset.${name}.hint`)}
                          {row && (
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                fontFamily: "monospace",
                                opacity: 0.7,
                              }}
                            >
                              [carence={row.carence}, avoid={row.avoid}, wish=
                              {row.wish}, equity={row.equity}]
                            </Box>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </RadioGroup>
          </FormControl>

          <Accordion
            expanded={advanced}
            onChange={(_, v) => setAdvanced(v)}
            elevation={0}
            sx={{ border: 1, borderColor: "divider" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("generate.advanced")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <WeightSlider
                  label={t("generate.weight.carence")}
                  value={carence}
                  onChange={setCarence}
                  min={0}
                  max={1000}
                  step={10}
                />
                <WeightSlider
                  label={t("generate.weight.avoid")}
                  value={avoid}
                  onChange={setAvoid}
                  min={0}
                  max={100}
                  step={1}
                />
                <WeightSlider
                  label={t("generate.weight.wish")}
                  value={wish}
                  onChange={setWish}
                  min={0}
                  max={100}
                  step={1}
                />
                <WeightSlider
                  label={t("generate.weight.equity")}
                  value={equity}
                  onChange={setEquity}
                  min={0}
                  max={100}
                  step={1}
                />
                <Typography variant="caption" color="text.secondary">
                  {t("generate.advancedHint")}
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleConfirm}
          disabled={busy}
        >
          {busy ? t("planning.generating") : t("generate.runCta")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function WeightSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
}) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">{label}</Typography>
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {value}
        </Typography>
      </Stack>
      <Slider
        size="small"
        value={value}
        onChange={(_, v) => {
          const next = Array.isArray(v) ? v[0] : v;
          if (typeof next === "number") onChange(next);
        }}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay="auto"
      />
    </Box>
  );
}
